import React, { useState } from 'react';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Input from '@material-ui/core/Input';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import Radio from '@material-ui/core/Radio';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import { Typography } from '@material-ui/core';
import clsx from 'clsx';
import { ActionIcon } from '@mummssoftware/common-ui';
import { getCPPTextEngine } from '@mummssoftware/utils/utils';
import { checkSubqchoices } from '@mummssoftware/utils/form-engine/form-engine-utils';
import { useStyles } from './style';
import { getOtherOption } from './utils';
import { Other } from './constants';
import { Text, OtherInputDiv, TitleOtherCpp } from './WrapperDiv';

type LabelFieldProps = {
  text: string;
  key: string;
  checked?: boolean;
  onClick?: any;
  isExpanded?: boolean;
};

type InputFieldProps = {
  value: string;
  label: string;
  subqchoice?: any;
  key: string;
  disabled?: boolean;
  handleChange?: any;
  isOtherComment?: boolean;
};
type InputTitleFieldProps = {
  value: string | null;
  handleTitleChange?: any;
};

type FieldProps = {
  checked: boolean;
  key: string;
  subqchoice: any;
  handleChange?: any;
  readonly?: boolean;
};
type SelectFieldProps = {
  key: string;
  checked: any;
  subqchoice: any;
  handleChange?: any;
  handleCommentChange?: any;
  state?: any;
};

type DCProps = {
  key: string;
  handleChange?: any;
  radio?: boolean;
  disabled?: boolean;
  outsideDC?: boolean;
};

export const LabelField = ({ text, onClick, isExpanded }: LabelFieldProps) => {
  const [expanded, setExpanded] = useState<boolean>(isExpanded || false);
  const classes = useStyles();

  const handleClick = () => {
    setExpanded(!expanded);
    onClick();
  };
  return (
    <Text>
      <ActionIcon
        style={{
          margin: 0,
        }}
        Icon={expanded ? ExpandMoreIcon : ChevronRightIcon}
        onClick={handleClick}
        value={expanded}
      />
      <Typography
        variant="body1"
        classes={{
          root: classes.fieldLabel,
        }}
      >
        {text}
      </Typography>
    </Text>
  );
};

const iconStyle = {
  margin: 0,
  '@media print': {
    display: 'none',
  },
};

// ReadOnly Form
export const ReadLabelField = ({ text, isExpanded }: LabelFieldProps) => {
  const classes = useStyles();

  return (
    <Text>
      <ActionIcon
        style={iconStyle}
        Icon={isExpanded ? ExpandMoreIcon : ChevronRightIcon}
        id="icon-cpp-idg"
      />
      <Typography
        variant="body1"
        classes={{
          root: classes.readLabel,
        }}
      >
        {text}
      </Typography>
    </Text>
  );
};

export const InputField = ({
  handleChange,
  label,
  subqchoice,
  disabled,
  value,
  isOtherComment,
}: InputFieldProps) => {
  const classes = useStyles();
  const asEvidencedBy = 'As evidenced by';
  // is the comment input for GOALS
  const isGoalClass =
    subqchoice &&
    (subqchoice.questionclass.toLowerCase() === 'goals' ||
      subqchoice.questionclass.toLowerCase() === 'goal');
  /** format the string */
  let formattedInput = getCPPTextEngine(value);
  // empty comment
  if (isGoalClass && formattedInput === '') {
    formattedInput = `${asEvidencedBy} ${formattedInput}`;
  }
  const [textValue, setValue] = useState<string>(formattedInput);

  const handleTextChange = (event: any) => {
    setValue(event.target.value);
  };

  const handleOnSave = (e: any) => {
    handleChange(textValue);
    // handleChange(e.target.value);
  };
  return (
    <TextField
      id="text-field-cpp"
      label={label || 'Comment'}
      variant="filled"
      multiline
      value={textValue}
      disabled={disabled}
      onChange={handleTextChange}
      onBlur={handleOnSave}
      className={isOtherComment ? undefined : clsx(classes.largeTextField)}
      InputProps={{
        classes: { root: classes.rootInput },
      }}
      InputLabelProps={{
        shrink: true,
      }}
    />
  );
};

/**
 *  Functions for Select and Multi Select
 */
const getLabels = (hasValue: boolean, subqchoices: any[]) =>
  hasValue
    ? subqchoices.reduce((seed: any, value: any) => {
        seed[value.questionpath] = value.questionlabel;
        return seed;
      }, {})
    : {};
// keys is ['27571->25005->25609','27571->25005->25610']
// names is {'27571->25005->25609': 'Patient','27571->25005->25610': 'Other'}
const hasCommentInput = (keys: string[], names: Record<string, string>) => {
  let hasComment = false;
  if (keys.length) {
    const selected = keys.map((key: string) => names[key]);
    if (selected.includes(Other)) {
      hasComment = true;
    }
  }
  return hasComment;
};

const getCommentValue = (otherInput: any, state: any) => {
  const otherparentquestionpath = otherInput
    ? otherInput.parentquestionpath
    : null;
  const otherquestionpath = otherInput ? otherInput.questionpath : null;

  const commentValue =
    otherparentquestionpath && state[otherparentquestionpath]
      ? state[otherparentquestionpath][otherquestionpath]
      : '';
  return commentValue;
};
/**
 *  We want the input to show on SELECTING Other in dropdowns
 * Following recursion it shows when the Other element has already been selected -
 * It does not show if the element has not been selected
 * behavior is to show it upon direct action a
 */
const getCommentField = (
  hasComment: boolean,
  subqchoice: any,
  state: any,
  handleCommentChange: any,
) => {
  const otherInput = getOtherOption(subqchoice);
  const isNewForm = otherInput ? otherInput.isothercomment : false;

  return (
    hasComment &&
    isNewForm && (
      <OtherInputDiv>
        <InputField
          isOtherComment
          key={`field-${otherInput.id}`}
          label={otherInput.questionlabel}
          value={getCommentValue(otherInput, state) || ''}
          handleChange={handleCommentChange}
        />
      </OtherInputDiv>
    )
  );
};
/**
 */

export const SelectField = ({
  checked,
  subqchoice,
  handleChange,
  handleCommentChange,
  state,
}: SelectFieldProps) => {
  const classes = useStyles();
  const hasValue = checkSubqchoices(subqchoice);

  let selectValue = '';
  // keys = [] or ['27571->25005->25609']
  const keys = Object.keys(checked);
  if (keys.length) {
    // eslint-disable-next-line prefer-destructuring
    selectValue = keys[0];
  }

  const { subqchoices } = subqchoice;

  const names = getLabels(hasValue, subqchoices);
  const hasComment = hasCommentInput(keys, names);

  return (
    // if autounroll this is the parent node
    subqchoice.autounroll && (
      <>
        <FormControl className={classes.formControl}>
          <InputLabel shrink>{`${subqchoice.questionlabel}`}</InputLabel>
          <Select
            value={hasValue && selectValue}
            onChange={handleChange}
            displayEmpty
            name={subqchoice.questionlabel}
          >
            {hasValue &&
              subqchoice.subqchoices.map((choice: any) => (
                <MenuItem
                  key={choice.questionlabel}
                  value={choice.questionpath}
                >
                  {`${choice.questionlabel}`}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {getCommentField(hasComment, subqchoice, state, handleCommentChange)}
      </>
    )
  );
};

export const MultiSelectField = ({
  checked,
  subqchoice,
  handleChange,
  handleCommentChange,
  state,
}: SelectFieldProps) => {
  const classes = useStyles();
  const { subqchoices } = subqchoice;
  const hasValue = checkSubqchoices(subqchoice);
  let selectValue: any = [];
  // checked is {27563->25040->25357->24979->24965: true}
  // keys = [] or ['27571->25005->25609']
  const keys = Object.keys(checked);
  if (keys.length) {
    // eslint-disable-next-line prefer-destructuring
    selectValue = keys;
  }
  const names = getLabels(hasValue, subqchoices);
  const hasComment = hasCommentInput(keys, names);

  const MenuProps = {
    variant: 'menu',
    getContentAnchorEl: null, // to get rid of the jumping.
    // https://stackoverflow.com/questions/59785482/multiselect-box-popover-keeps-jumping-when-scroll-or-select-items
  };

  return (
    // if autounroll this is the parent node
    subqchoice.autounroll && (
      <>
        <FormControl className={classes.formControl}>
          <InputLabel shrink>{subqchoice.questionlabel}</InputLabel>
          <Select
            value={selectValue}
            onChange={handleChange}
            displayEmpty
            multiple
            input={<Input />}
            renderValue={(selected: any) => {
              const values = selected.map((el: string) => names[el]);
              return values.join(', ');
            }}
            name={subqchoice.questionlabel}
            MenuProps={MenuProps as any}
          >
            {hasValue &&
              subqchoices.map((choice: any) => (
                <MenuItem
                  key={choice.questionlabel}
                  value={choice.questionpath}
                >
                  <Checkbox
                    checked={!!checked[choice.questionpath]}
                    color="primary"
                    classes={{
                      root: classes.defaultColor,
                    }}
                    name={choice.questionlabel}
                  />
                  <ListItemText primary={choice.questionlabel} />
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {getCommentField(hasComment, subqchoice, state, handleCommentChange)}
      </>
    )
  );
};

export const CheckBoxField = ({
  checked,
  subqchoice,
  handleChange,
  readonly,
}: FieldProps) => {
  const classes = useStyles();
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={handleChange}
          name={subqchoice.message}
          classes={
            readonly
              ? {
                  root: classes.readOnly,
                }
              : {
                  root: classes.defaultColor,
                }
          }
          color="primary"
        />
      }
      label={`${subqchoice.questionlabel}`}
      key={subqchoice.id}
      classes={{
        label: classes.label,
        root: classes.label,
      }}
    />
  );
};

export const RadioField = ({
  checked,
  subqchoice,
  handleChange,
  readonly,
}: FieldProps) => {
  const classes = useStyles();
  return (
    <FormControlLabel
      key={subqchoice.questionpath}
      name="radio-cpp"
      control={
        <Radio
          id={subqchoice.questionpath}
          size="small"
          color="primary"
          value={subqchoice.questionpath}
          name="radio-cpp"
          checked={checked}
          onChange={handleChange}
          classes={
            readonly
              ? {
                  root: classes.readOnly,
                }
              : {
                  root: classes.defaultColor,
                }
          }
        />
      }
      label={subqchoice.questionlabel}
      classes={{
        label: classes.label,
        root: classes.label,
      }}
    />
  );
};

export const DCButton = ({
  handleChange,
  radio,
  outsideDC,
  disabled,
}: DCProps) => {
  const classes = useStyles();
  // eslint-disable-next-line no-nested-ternary
  const rootClass = outsideDC
    ? classes.outsideDC
    : radio
    ? classes.radioButtonRoot
    : classes.buttonRoot;
  return (
    <Button
      type="submit"
      variant="outlined"
      size="small"
      onClick={handleChange}
      disabled={disabled}
      color="primary"
      classes={{
        label: classes.buttonLabel,
        root: rootClass,
        outlined: classes.buttonOutlined,
      }}
    >
      DC
    </Button>
  );
};

export const CPPTitleInputField = ({
  handleTitleChange,
  value,
}: InputTitleFieldProps) => {
  const classes = useStyles();
  const [textValue, setValue] = useState<string | null>(value);

  const handleTextChange = (event: any) => {
    setValue(event.target.value);
  };

  return (
    <TitleOtherCpp>
      <TextField
        id="title-field-cpp"
        label="Title"
        variant="filled"
        multiline
        value={textValue}
        onChange={handleTextChange}
        onBlur={handleTitleChange}
        className={clsx(classes.halfTextField)}
        InputProps={{
          classes: { root: classes.rootInput },
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />
    </TitleOtherCpp>
  );
};
